import XCircle from 'components/Icons/XCircle';
import { useEffect, useRef } from 'react';

import Button from '@/components/DesignLibrary/Button';
import Portal from '@/components/Portal';
import Spacer from '@/components/Spacer';

import s from './ConfirmationDialog.module.scss';

const ConfirmationDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  dialogProps: {
    message: string;
    confirmText?: string;
    cancelText?: string;
    variant?: 'primary' | 'destructive';
    alert?: boolean;
  };
}> = ({ isOpen, onClose, onConfirm, dialogProps }) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && dialogRef.current) {
      const animationFrameId = requestAnimationFrame(() => {
        const elementsToSelect = 'button';
        const focusableElements = dialogRef.current
          ? Array.from(dialogRef.current.querySelectorAll<HTMLElement>(elementsToSelect))
          : [];
        if (focusableElements.length > 0) {
          const lastFocusableElement = focusableElements[focusableElements.length - 1];
          lastFocusableElement.focus();
        } else {
          dialogRef.current?.focus();
        }
      });

      return () => cancelAnimationFrame(animationFrameId);
    }

    return () => {};
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return () => {};

    const handleDocumentKeyDown = (event: KeyboardEvent): void => {
      // Alert dialogs should not be dismissible via Escape key
      if (event.key === 'Escape' && !dialogProps.alert) {
        onClose();
        event.stopPropagation();
        event.preventDefault();
      }
    };

    const previouslyFocusedElement = document.activeElement as HTMLElement;

    document.addEventListener('keydown', handleDocumentKeyDown);

    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown);
      if (previouslyFocusedElement && previouslyFocusedElement.focus) {
        previouslyFocusedElement.focus();
      }
    };
  }, [isOpen, onClose, dialogProps.alert]);

  const handleBackdropClick = (e: React.MouseEvent): void => {
    // Alert dialogs should not be dismissible via backdrop click
    if (e.target === e.currentTarget && !dialogProps.alert) {
      onClose();
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = async (event) => {
    if (event.key === 'Tab') {
      const modal = event.currentTarget;
      const elementsToSelect = 'button';
      const focusableElements = modal.querySelectorAll<HTMLElement>(elementsToSelect);
      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          event.preventDefault();
          lastFocusableElement.focus();
        }
      } else if (document.activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className={s.backdrop} onClick={handleBackdropClick}>
        <div
          className={s.dialog}
          onKeyDown={handleKeyDown}
          ref={dialogRef}
          role={dialogProps.alert ? 'alertdialog' : 'dialog'}
          aria-modal='true'
          aria-labelledby='confirmation-dialog-message'
        >
          {!dialogProps.alert && (
            <button className={s.closeButton} onClick={onClose}>
              <XCircle weight='fill' size={24} />
            </button>
          )}
          <p className={s.message} id='confirmation-dialog-message'>
            {dialogProps.message}
          </p>
          <Spacer />
          <div className={s.buttons}>
            {!dialogProps.alert && (
              <Button variant='secondary' onClick={onClose} size='sm'>
                {dialogProps.cancelText || 'Cancel'}
              </Button>
            )}
            <Button variant={dialogProps.variant || 'destructive'} size='sm' onClick={onConfirm}>
              {dialogProps.confirmText || 'Confirm'}
            </Button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default ConfirmationDialog;
